import type { SkillFileDiff, SkillFinding } from "@roomate/core";

/** Wire shapes of the skills-library routes (docs/specs/skills-library.md, "Hub" API table). */
export type SkillVersionStatus = "pending" | "approved" | "rejected" | "superseded";

export type SkillListItemDTO = {
  id: string;
  name: string;
  /** Display form of the source: the GitHub URL, or the uploaded zip's name. */
  source: string;
  /** Latest approved version number, or null while nothing is approved. */
  approvedVersion: number | null;
  /** Id of the newest pending version, so the list can open its review. */
  pendingVersionId: string | null;
  pendingCount: number;
  usedBy: number;
  archivedAt: number | null;
};

export type SkillVersionSummaryDTO = {
  id: string;
  version: number;
  status: SkillVersionStatus;
  sourceRef: string;
  fetchedAt: number;
  decidedAt: number | null;
};

export type SkillDetailDTO = {
  skill: SkillListItemDTO & { serviceHosts: string[] };
  versions: SkillVersionSummaryDTO[];
  /** Ids of the assigned, non-archived agents. */
  agentIds: string[];
};

export type SkillVersionDTO = {
  id: string;
  skillId: string;
  name: string;
  version: number;
  status: SkillVersionStatus;
  source: string;
  sourceRef: string;
  serviceHosts: string[];
  files: { path: string; text: string }[];
  findings: SkillFinding[];
  /** Per-file diff against the approved version; null when there is none to compare with. */
  diff: SkillFileDiff[] | null;
  rejectReason: string | null;
  /** Hook ids already approved on this version (once decided). */
  approvedHookIds: string[];
};

/** One skill folder found in a multi-skill source; the admin picks which to add. */
export type SkillFolderDTO = { path: string; name: string };

export type SkillFetchResult = { versions: { id: string; skillId: string; name: string; version: number }[]; choose?: SkillFolderDTO[] };

export type HookVerdict = "approve" | "reject";
export type HookVerdicts = Record<string, HookVerdict>;

export type HookFinding = Extract<SkillFinding, { kind: "hook" }>;

export const isHookFinding = (f: SkillFinding): f is HookFinding => f.kind === "hook";

export const hookFindings = (findings: readonly SkillFinding[]): HookFinding[] => findings.filter(isHookFinding);

/** Approve stays disabled until every hook finding has its own approve/reject verdict. */
export function canApprove(findings: readonly SkillFinding[], verdicts: HookVerdicts): boolean {
  return hookFindings(findings).every((f) => verdicts[f.detail.hookId] !== undefined);
}

export const SOURCE_NOTE = "Flagged hosts are a review aid, not enforcement.";

/** `?add=<url>` value for a source the admin picked elsewhere, e.g. the Inbox "Add to Skills library" button. */
export const addLink = (url: string) => `/skills?add=${encodeURIComponent(url)}`;

/** Splits "a.com, b.com" style text into unique non-empty hosts. */
export function parseHosts(text: string): string[] {
  const bare = (h: string) => h.toLowerCase().replace(/^[a-z][a-z0-9+.-]*:\/\//, "").replace(/[/?#].*$/, "").replace(/^[^@]*@/, "").replace(/:\d+$/, "").replace(/\.+$/, "");
  return [...new Set(text.split(/[\s,]+/).map(bare).filter(Boolean))];
}
